import React, {useEffect, useRef, useState} from "react";
import {connect, useSelector} from "react-redux";
import {useTranslation} from 'react-i18next';
import {bindActionCreators} from "redux";
import {Container, Grid, Paper, Typography, Box, Button} from '@mui/material';

import {CONTENT_ITEM_TYPE, Course, Lesson, TEST_STATE, Task} from "../../../common/course";
import {CommonState} from "../../../store";
import {selectLessonById, selectLessonsByCourseId, selectTasksByCourseId, selectTestSubmitionById} from "../../../store/course/selectors";
import courseActions from '../../../store/course/actions';
import ContentNode from "./ContentNode";

type Props = {
    markTaskAsVisited: Function;
    task: Task;
    onNextTask: Function
    courseId: Course['id'];
}
const View = ({markTaskAsVisited, task, onNextTask, courseId}: Props) => {
    const [viewedTaskId, setViewed] = useState<string | undefined>();
    const progressMarkerEdge = useRef(null);
    const tasks: Task[] = useSelector((state: CommonState) => selectTasksByCourseId(state, String(courseId)));
    const lesson: Lesson = useSelector((state: CommonState) => selectLessonById(state, String(task?.lessonId)));
    const lessons: Lesson[] = useSelector((state: CommonState) => selectLessonsByCourseId(state, String(courseId)));
    const [nextTaskId, setNextTaskId]= useState<Task['id'] | undefined>(undefined);
    const {t: translate, i18n: languageCtx} = useTranslation();
    const NEXT_LESSON_BTN_TEXT = translate('translation:nextLessonBtn');
    const NEXT_TASK_BNT_TEXT = translate('translation:nextBtn');
    const [nextBtnText, setNextBtnText] = useState<string>(NEXT_TASK_BNT_TEXT);
    //@ts-expect-error
    const getTestContent = content => content.find(content => content.type === CONTENT_ITEM_TYPE.TEST) 
    const testSubmition = useSelector((state: CommonState) => selectTestSubmitionById(state, getTestContent(task.content)?.testId));
    const options = {
        root: null,
        rootMargin: '0px',
        threshold: 1.0,
    }
    //@ts-expect-error
    const isTestTask = (task) => Boolean(getTestContent(task.content));

    //@ts-expect-error
    const onMarkerViewed = (entries) => {
        const {results: {maxScore, score}, id} = task;
        if (viewedTaskId === id || score === maxScore) {
            return;
        }
        const [entry] = entries;
        const isVisible = entry.isIntersecting;
        if (isVisible) {
            setViewed(id);
            markTaskAsVisited({lessonId: task.lessonId, taskId: task.id});
        }
    }
    const onNextTaskHandle = () => {
        const next = nextTaskId;
        setNextTaskId(undefined);
        onNextTask(next);
    }

    useEffect(() => {
        const observer = new IntersectionObserver(onMarkerViewed, options);
        if (progressMarkerEdge.current) {
            observer.observe(progressMarkerEdge.current)
        }
        return () => {
            if (progressMarkerEdge.current) {
                observer.unobserve(progressMarkerEdge.current)
            }
        };
    }, [progressMarkerEdge, options, lessons]);
    // update next taskId
    useEffect(() => {
        const nextLesson = lessons.find((lessonItem: Lesson) => lessonItem.id === lesson.next);
        if (!task?.nextTaskId && !nextLesson) {
            setNextTaskId(undefined);
            return;
        }
        if (!task) {
            return;
        }
        if (task.nextTaskId) {
            setNextTaskId(task.nextTaskId);
            setNextBtnText(NEXT_TASK_BNT_TEXT);
        } else {
            
            if (!nextLesson) {
                return;
            }
            setNextBtnText(NEXT_LESSON_BTN_TEXT);
            setNextTaskId(tasks.find(task => task.lessonId === nextLesson.id)?.id);
        }
    }, [task, lesson, lessons]);
    return (
        <> 
                {task && (
                <Grid item xs={12}>
                    <Paper variant="outlined" sx={{p: 2, mb: 2}}>
                        <Typography
                            component="h2"
                            sx={{
                                textAlign: 'center',
                                fontWeight: 'bold',
                                textTransform: 'uppercase',
                                fontSize: '1.5rem',
                            }}
                        >
                            {task.title}
                        </Typography>
                        {task.content.map((content, index) => (
                            <ContentNode
                             key={index}
                             content={content}
                            />
                        ))}
                        {!isTestTask(task) && (
                            <div ref={progressMarkerEdge}></div>
                        )}
                        {nextTaskId && (
                            isTestTask(task) ?
                            testSubmition?.state === TEST_STATE.SUCCESS && (
                                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                                    <Button variant="contained" onClick={() => onNextTaskHandle()}>{nextBtnText}</Button>
                                </Box>
                            )
                            : (
                                <Box sx={{display: 'flex', justifyContent: 'center'}}>
                                    <Button variant="contained" onClick={() => onNextTaskHandle()}>{nextBtnText}</Button>
                                </Box>
                            )
                        )}
                    </Paper>
                </Grid>)}
        </>
    )
}

//@ts-expect-error
const dispatchProps = (dispatch) => ({
    markTaskAsVisited: bindActionCreators(courseActions.markTaskAsVisited, dispatch),
})

export default connect(null, dispatchProps)(View);